import { FC, useEffect, useState } from 'react'
import { Box, CircularProgress, Paper } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Stack, useTheme } from '@mui/system'
import DashboardToolbar, { DashboardFilter } from '../../components/features/AdssDashboard/DashboardToolbar'
import StatusPanel from '../../components/features/AdssDashboard/StatusPanel'
import Map from '../../components/features/AdssDashboard/Map'
import Kpi from '../../components/features/AdssDashboard/Kpi'
import ActivityLog from '../../components/features/AdssDashboard/ActivityLog'
import { useParams } from 'react-router'
import { DashboardConfig } from '../../types/dashboard.type'
import { screenshotConfigs } from './screenshot-configs'
import { getPlaybookReport } from '../../services/playbook.service'

interface IProps {}

const DASHBOARD_REFRESH_INTERVAL = 5 * 1000

export const dashboards: {
  id: number
  name: string
  config?: DashboardConfig
  playbook?: string | number
  report?: string
}[] = [
  {
    id: 1,
    name: 'Interference report',
    playbook: 'interference-report',
    report: 'dashboard',
  },
  {
    id: 2,
    name: 'Stop Buzzer Status',
    playbook: 'stop-buzzer',
    report: 'dashboard',
  },
  ...screenshotConfigs,
]

const Dashboard: FC<IProps> = () => {
  const theme = useTheme()
  const [config, setConfig] = useState<DashboardConfig | null>(null)
  const [filter, setFilter] = useState<DashboardFilter | null>(null)
  const [error, setError] = useState<string | null>('')

  const { dashboardId = '0' } = useParams()

  useEffect(() => {
    if (filter === null) {
      // filter is not initialized
      return
    }

    const dashboard = dashboards.find((d) => d.id === Number(dashboardId))

    if (!dashboard) {
      setConfig(null)
      setError('Dashboard not found')
      return
    }

    if (dashboard.config) {
      setConfig(dashboard.config)
      setError(null)
      return
    }

    if (dashboard.playbook && dashboard.report) {
      const loadDashboard = () => {
        if (!dashboard.playbook || !dashboard.report) {
          return
        }

        setError(null)
        getPlaybookReport(dashboard.playbook, dashboard.report, filter)
          .then((report) => setConfig(report))
          .catch(() => setError('Loading failed'))
      }

      const intervalId = setInterval(loadDashboard, DASHBOARD_REFRESH_INTERVAL)

      loadDashboard() // initial loading

      return () => clearInterval(intervalId)
    }

    setConfig(null)
    setError('Invalid dashboard')
  }, [dashboardId, filter])

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} dateFormats={{}}>
      <Box
        minWidth="1100px"
        maxWidth="1600px"
        margin="auto"
        sx={
          theme.palette.mode === 'light' ? { '& div.MuiPaper-root': { background: theme.palette.elevated.main } } : null
        }
      >
        <Stack margin={2} spacing={2}>
          <Paper sx={{ padding: 2 }}>
            <DashboardToolbar onFilterChange={setFilter} />
          </Paper>

          {config ? (
            <>
              <Stack direction="row" spacing={2} alignItems="stretch">
                <StatusPanel fields={config.status} />
                <Paper
                  sx={{
                    p: 0,
                    flexGrow: 1,
                    minHeight: '540px',
                    display: 'flex',
                    '& .red-label': { background: 'red' },
                  }}
                >
                  <Map {...config.map} />
                </Paper>
                <Kpi boxes={config.kpi} />
              </Stack>

              <Paper sx={{ p: 2 }}>
                <ActivityLog rows={config.activity || []} />
              </Paper>
            </>
          ) : (
            <Box display="flex" justifyContent="center" padding={10}>
              {error || <CircularProgress size={28} color="inherit" />}
            </Box>
          )}
        </Stack>
      </Box>
    </LocalizationProvider>
  )
}

export default Dashboard
